import {
  WSElement,
  WSElementProps,
  WSInfiniteScroll,
  WSTable,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  ICollaboratorSchema,
  MemberClientStatus
} from "@wingspanhq/payments/dist/interfaces";
import React, { useState } from "react";
import { Route, useHistory } from "react-router-dom";
import { WSPersistentUpgradeButton } from "../../../components/Membership/WSPersistentUpgradeButton";
import {
  getCollaboratorCompany,
  getCollaboratorEmail,
  getCollaboratorName
} from "../../../query/payments/selectors";
import { WSFrontendFeature } from "../../../Settings/utils/subscriptionUtils";
import { openInNewTab } from "../../../utils/openInNewTab";
import { InvoicesCollaboratorDetails } from "../../screens/InvoicesCollaboratorDetails";
import { ScheduledDeductionDetails } from "../../screens/deductions/ScheduledDuductionDetails";
import { CompletedDeductionDetails } from "../../screens/deductions/CompletedDuductionDetails";
import NEC1099FilingDetailsSummary, {
  NEC_1099_SUMMARY_SLUG
} from "../../../1099NECFiling/screens/NEC1099FilingDetailsSummary";
import { MemberIdUrlKey } from "../../../constants/routing";
import { NECSummaryDataType } from "../../../1099NECFiling/screens/NEC1099FilingDetailsSummary/Content";

type Props = WSElementProps & {
  collaborators: ICollaboratorSchema[];
  listPath: string;
};

const PAGE_SIZE = 20;
export const CollaboratorsTable: React.FC<Props> = ({
  collaborators,
  listPath,
  ...elementProps
}) => {
  const history = useHistory();
  const [page, setPage] = useState(0);

  return (
    <>
      <Route
        path={`${listPath}/:${MemberIdUrlKey.Collaborator}`}
        component={InvoicesCollaboratorDetails}
        exact
      />
      <Route
        path={`${listPath}/:${MemberIdUrlKey.Collaborator}/deduction/scheduled/:deductionId`}
        component={ScheduledDeductionDetails}
      />
      <Route
        path={`${listPath}/:${MemberIdUrlKey.Collaborator}/deduction/completed/:deductionId`}
        component={CompletedDeductionDetails}
      />
      <Route
        path={`${listPath}/:${MemberIdUrlKey.Collaborator}/${NEC_1099_SUMMARY_SLUG}`}
      >
        <NEC1099FilingDetailsSummary type={NECSummaryDataType.Collaborator} />
      </Route>

      <WSInfiniteScroll
        onLoad={() => {
          setPage(p => ++p);
        }}
        loadMore={collaborators.length > page * PAGE_SIZE}
        endOfList={collaborators.length <= page * PAGE_SIZE}
        loading={false}
      >
        <WSTable
          showIcon
          tableData={collaborators
            .slice(0, page * PAGE_SIZE)
            .map(collaborator => ({
              id: collaborator.collaboratorId,
              data: collaborator,
              icon: {
                text: getCollaboratorName(collaborator)
              }
            }))}
          columns={[
            {
              config: {
                gridTemplateSizeMax: "1fr"
              },
              renderFunction: ({ data }) => {
                const company = getCollaboratorCompany(data);
                return (
                  <>
                    <WSText mb="XS">{getCollaboratorName(data)}</WSText>
                    {company && <WSText.Heading5>{company}</WSText.Heading5>}
                  </>
                );
              }
            },
            {
              config: {
                hideOnScreens: ["XS"],
                gridTemplateSizeMax: "0.5fr"
              },
              renderFunction: ({ data }) => (
                <WSText>{getCollaboratorEmail(data)}</WSText>
              )
            },
            {
              config: {
                hideOnScreens: ["XS"],
                gridTemplateSizeMax: "0.7fr",
                justify: "end"
              },
              renderFunction: ({ data }) =>
                data.status === MemberClientStatus.Pending ? (
                  <WSElement>
                    <WSElement>
                      <WSText.Label inline>
                        Setup not started.
                        {/* <WSText.Heading4
                        ml="XS"
                        inline
                        color="blue400"
                        onClick={() => {
                          sendCollaboratorInvite(data.collaboratorId, {
                            onSuccess: () => {
                              openSnackbar({
                                message: `Invite re-sent to ${data.member.user
                                  .profile?.firstName ||
                                  data.member.user.email}`
                              });
                            }
                          });
                        }}
                      >
                        Resend invite
                      </WSText.Heading4>*/}
                      </WSText.Label>
                    </WSElement>

                    <WSText.Heading5 color="yellow500">
                      Not yet eligible for payments
                    </WSText.Heading5>
                  </WSElement>
                ) : (
                  <WSPersistentUpgradeButton
                    feature={WSFrontendFeature.InviteAndPayCollaborator}
                    kind="Link"
                    onClick={() => {
                      history.push("/member/invoices/payables/create", {
                        memberClientId: data.collaboratorId
                      });
                    }}
                  >
                    Send payment
                  </WSPersistentUpgradeButton>
                )
            }
          ]}
          onRowClick={item => {
            history.push(`${listPath}/${item.id}`, {
              backPath: listPath
            });
          }}
          onRowControlClick={item => {
            openInNewTab(`${listPath}/${item.id}`);
          }}
          onRowMousewheelClick={item => {
            openInNewTab(`${listPath}/${item.id}`);
          }}
          data-testid="collaboratorsTable"
          {...elementProps}
        />
      </WSInfiniteScroll>
    </>
  );
};
